import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#1b5e20';
const PRIMARY_MID = '#2e7d32';
const PRIMARY_LT  = '#e8f5e9';

const fmtQ = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtV = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const SalesAnalysisReport = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate]     = useState(fmt2Date(today));
    const [data, setData]         = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [fetched, setFetched]   = useState(false);
    const [search, setSearch]     = useState('');

    useEffect(() => {
        if (companyId && branchId && token && fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setData([]);
        setFetched(false);
        setError('From Date cannot be greater than To Date');
        return;
      }
      fetchReport();
    }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'SalesAnalysisReportApi',
                `/SalesAnalysis/${fromDate}/${toDate}/${companyId}/${branchId}`,
                token
            );
            const rows = res?.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch sales analysis report');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.toLowerCase();
        return data.filter(r => (r.ItemName || '').toLowerCase().includes(q));
    }, [data, search]);

    const totals = useMemo(() => filtered.reduce((acc, r) => ({
        SoldQty:      acc.SoldQty      + (parseFloat(r.SoldQty)      || 0),
        ReturnQty:    acc.ReturnQty    + (parseFloat(r.ReturnQty)    || 0),
        NetQty:       acc.NetQty       + (parseFloat(r.NetQty)       || 0),
        SoldAmount:   acc.SoldAmount   + (parseFloat(r.SoldAmount)   || 0),
        ReturnAmount: acc.ReturnAmount + (parseFloat(r.ReturnAmount) || 0),
        NetAmount:    acc.NetAmount    + (parseFloat(r.NetAmount)    || 0),
    }), { SoldQty: 0, ReturnQty: 0, NetQty: 0, SoldAmount: 0, ReturnAmount: 0, NetAmount: 0 }), [filtered]);

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'SalesAnalysis', 0);
        if (!filtered.length) return;
        const headers = ['#', 'Item Name', 'Sold Qty', 'Return Qty', 'Net Qty', 'Sold Amount', 'Return Amount', 'Net Amount'];
        const rows = filtered.map((r, i) => [
            i + 1, r.ItemName,
            r.SoldQty, r.ReturnQty, r.NetQty,
            r.SoldAmount, r.ReturnAmount, r.NetAmount,
        ]);

        await exportStyledExcel({
            reportTitle: 'SALES ANALYSIS REPORT',
            sheetName: 'Sales Analysis',
            fileName: `SalesAnalysis_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            totalsRow: ['', 'Totals',
                fmtQ(totals.SoldQty), fmtQ(totals.ReturnQty), fmtQ(totals.NetQty),
                fmtV(totals.SoldAmount), fmtV(totals.ReturnAmount), fmtV(totals.NetAmount)],
            columnWidths: [4, 30, 12, 12, 12, 16, 16, 16],
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #388e3c', color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading sales analysis..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Sales Analysis
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Sales Analysis Report</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <input placeholder="Search item..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 160, outline: 'none' }} />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary bar */}
                {fetched && filtered.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Items',          value: filtered.length,              color: PRIMARY },
                            { label: 'Sold Qty',       value: fmtQ(totals.SoldQty),         color: '#0277bd' },
                            { label: 'Return Qty',     value: fmtQ(totals.ReturnQty),       color: '#dc2626' },
                            { label: 'Net Qty',        value: fmtQ(totals.NetQty),          color: PRIMARY },
                            { label: 'Sold Amount',    value: fmtV(totals.SoldAmount),      color: '#0277bd' },
                            { label: 'Return Amount',  value: fmtV(totals.ReturnAmount),    color: '#dc2626' },
                            { label: 'Net Amount',     value: fmtV(totals.NetAmount),       color: '#059669' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Meta */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {fromDate} to {toDate} &nbsp;|&nbsp; {filtered.length} items
                        {search && ` (filtered from ${data.length})`}
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 190px)' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 800, width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                            <tr>
                                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 220 }}>Item Name</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Sold Qty</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Return Qty</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Net Qty</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Sold Amount</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Return Amount</th>
                                <th style={{ ...thBase, textAlign: 'right', background: '#1a3a1a' }}>Net Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : filtered.map((r, i) => {
                                const netAmt = parseFloat(r.NetAmount) || 0;
                                return (
                                    <tr key={`${r.ItemId}-${i}`} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT }}>
                                        <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                                        <td style={{ ...tdBase, fontWeight: 600, color: PRIMARY }}>{r.ItemName || '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd', fontWeight: 600 }}>{fmtQ(r.SoldQty)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#dc2626' }}>{parseFloat(r.ReturnQty) ? fmtQ(r.ReturnQty) : '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: PRIMARY, fontWeight: 600 }}>{fmtQ(r.NetQty)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd', fontWeight: 600 }}>{fmtV(r.SoldAmount)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#dc2626' }}>{parseFloat(r.ReturnAmount) ? fmtV(r.ReturnAmount) : '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: netAmt >= 0 ? '#059669' : '#dc2626' }}>{fmtV(r.NetAmount)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700, position: 'sticky', bottom: 0 }}>
                                    <td colSpan={2} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd', fontWeight: 700 }}>{fmtQ(totals.SoldQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>{fmtQ(totals.ReturnQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: PRIMARY, fontWeight: 700 }}>{fmtQ(totals.NetQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd', fontWeight: 700 }}>{fmtV(totals.SoldAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>{fmtV(totals.ReturnAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#059669', fontWeight: 700 }}>{fmtV(totals.NetAmount)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SalesAnalysisReport;
